import postHog from "@/config/posthog";
import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ARCADE_KEY_EVENT, advanceKonami, isEditableTarget, type ArcadeKey } from "./konami";

const ArcadeMenu = lazy(() => import("./arcade-menu").then((module) => ({ default: module.ArcadeMenu })));

class GameBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const FOCUSABLE = "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

/** Keeps Tab inside the overlay so focus never wanders into the app behind it. */
function cycleFocus(overlay: HTMLElement, backwards: boolean) {
  const targets = [...overlay.querySelectorAll<HTMLElement>(FOCUSABLE)];
  if (!targets.length) return;
  const current = targets.indexOf(document.activeElement as HTMLElement);
  const next =
    current < 0
      ? backwards
        ? targets.length - 1
        : 0
      : (current + (backwards ? -1 : 1) + targets.length) % targets.length;
  targets[next].focus();
}

/** Closing happens on key-down; the matching key-up must not reach app dialogs that close on Escape key-up. */
function swallowEscapeRelease() {
  const release = (event: KeyboardEvent) => {
    if (event.type === "keyup") {
      if (event.key !== "Escape") return;
      event.stopImmediatePropagation();
      event.preventDefault();
    }
    window.removeEventListener("keyup", release, true);
    window.removeEventListener("keydown", release, true);
  };
  window.addEventListener("keyup", release, true);
  window.addEventListener("keydown", release, true);
}

export function KonamiRuntime() {
  const { t } = useTranslation("arcade");
  const [open, setOpen] = useState(false);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    previousFocusRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (open) return;
    let progress = 0;
    const keydown = (event: KeyboardEvent) => {
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey || isEditableTarget(event.target)) {
        progress = 0;
        return;
      }
      const next = advanceKonami(progress, event.key);
      progress = next.progress;
      if (!next.complete) return;
      event.preventDefault();
      previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOpen(true);
      postHog.capture("EasterEggOpened");
    };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Capture keys even while the lazy chunk/font is still loading.
    const capture = (event: KeyboardEvent) => {
      event.stopImmediatePropagation();
      const overlay = overlayRef.current;
      if (event.key === "Tab") {
        event.preventDefault();
        if (event.type === "keydown" && overlay) cycleFocus(overlay, event.shiftKey);
        return;
      }
      // Buttons in the overlay (close, keyboard required) keep their native Enter/Space activation.
      const control = event.target instanceof Element ? event.target.closest(FOCUSABLE) : null;
      if (control && overlay?.contains(control) && (event.key === "Enter" || event.key === " ")) return;
      event.preventDefault();
      const detail: ArcadeKey = {
        key: event.key,
        code: event.code,
        down: event.type === "keydown",
        repeat: event.repeat,
      };
      const arcadeKey = new CustomEvent(ARCADE_KEY_EVENT, { detail, cancelable: true });
      document.dispatchEvent(arcadeKey);
      // Screens handle Escape themselves to step back; unhandled (e.g. while loading) it closes the arcade.
      if (detail.down && !detail.repeat && detail.key === "Escape" && !arcadeKey.defaultPrevented) {
        swallowEscapeRelease();
        close();
      }
    };
    window.addEventListener("keydown", capture, true);
    window.addEventListener("keyup", capture, true);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", capture, true);
      window.removeEventListener("keyup", capture, true);
    };
  }, [open, close]);

  if (!open) return null;
  const fallback = (message: string) => (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("Title")}
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center gap-6 bg-black text-white"
    >
      <p>{message}</p>
      <button type="button" onClick={close} className="rounded border px-4 py-2">
        {t("Close")}
      </button>
    </div>
  );
  return createPortal(
    <div ref={overlayRef}>
      <GameBoundary fallback={fallback(t("LoadError"))}>
        <Suspense fallback={fallback(t("Loading"))}>
          <ArcadeMenu onClose={close} />
        </Suspense>
      </GameBoundary>
    </div>,
    document.body,
  );
}
